// Mobile roster list (see `features/mobile-admin.md`): the company's athletes
// or coaches — one list per bottom-navigation tab — searchable by name or
// personal number. A floating action button registers a new person through the
// shared `AthleteDialog`; tapping a row opens the same dialog for editing. On
// the athletes tab each row also carries an enrol action that opens
// `MobileEnrollDialog` — the athlete-first counterpart of the attendance
// tab's class-first enrolment.

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import type {
  Athlete,
  TrainingClass,
  TrainingClassEnrollment,
} from '../domain';
import { athleteAge } from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';
import { AthleteDialog } from './AthleteDialog';
import { MobileEnrollDialog } from './MobileEnrollDialog';

interface MobileRosterViewProps {
  /** The company the roster (and any newly registered person) belongs to. */
  companyId: number;
  /** The company's full roster; split on the coach mark by `isCoach`. */
  athletes: Athlete[];
  /** Which list this tab shows: coaches (true) or athletes (false). */
  isCoach: boolean;
  /** Whether the signed-in user may register and edit people. */
  canManage: boolean;
  /** Called after a create/edit so the owner reloads the roster. */
  onChanged: () => void;
  /** The company's training classes (the per-athlete enrol picker). */
  classes: TrainingClass[];
  /** Live enrolments across all classes. */
  enrollments: TrainingClassEnrollment[];
  /** Whether the signed-in user may enrol athletes into classes. */
  canEnroll: boolean;
  /** Applies a refreshed company snapshot after an enrolment is added here. */
  onSnapshot: (snapshot: TrainingClassesSnapshot) => void;
}

export function MobileRosterView({
  companyId,
  athletes,
  isCoach,
  canManage,
  onChanged,
  classes,
  enrollments,
  canEnroll,
  onSnapshot,
}: MobileRosterViewProps) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Athlete | null>(null);
  // The athlete whose class-enrolment picker is open (athletes tab only).
  const [enrolling, setEnrolling] = useState<Athlete | null>(null);

  const roster = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return athletes
      .filter((a) => a.isCoach === isCoach)
      .filter(
        (a) =>
          needle.length === 0 ||
          a.fullName.toLowerCase().includes(needle) ||
          a.personalNumber.toLowerCase().includes(needle),
      );
  }, [athletes, isCoach, query]);

  const emptyKey = isCoach
    ? canManage
      ? 'admin.athletes.emptyCoachesEditable'
      : 'admin.athletes.emptyCoaches'
    : canManage
      ? 'admin.athletes.emptyAthletesEditable'
      : 'admin.athletes.emptyAthletes';

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (athlete: Athlete) => {
    setEditing(athlete);
    setDialogOpen(true);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('admin.trainingClasses.memberDialog.searchPlaceholder')}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {roster.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {t(
            athletes.some((a) => a.isCoach === isCoach)
              ? 'admin.trainingClasses.memberDialog.noMatches'
              : emptyKey,
          )}
        </Typography>
      ) : (
        <List disablePadding>
          {roster.map((athlete) => {
            const age = athleteAge(athlete.dateOfBirth);
            const details = [
              athlete.personalNumber,
              age === null ? null : t('admin.athletes.age', { count: age }),
            ]
              .filter((part) => part !== null)
              .join(' · ');
            return (
              <ListItem
                key={athlete.personalNumber}
                disablePadding
                secondaryAction={
                  !isCoach && canEnroll ? (
                    <IconButton
                      edge="end"
                      aria-label={t('admin.mobile.roster.enrollTitle', {
                        name: athlete.fullName,
                      })}
                      onClick={() => setEnrolling(athlete)}
                    >
                      <SchoolIcon />
                    </IconButton>
                  ) : undefined
                }
              >
                <ListItemButton
                  disabled={!canManage}
                  onClick={() => openEdit(athlete)}
                  sx={{ borderRadius: 2, px: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={athlete.photoUrls[0]} alt={athlete.fullName}>
                      {athlete.fullName.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={athlete.fullName}
                    secondary={details}
                    slotProps={{
                      primary: { noWrap: true },
                      secondary: { noWrap: true },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {canManage && (
        // Floats above the bottom navigation bar.
        <Box sx={{ position: 'fixed', right: 16, bottom: 76, zIndex: (th) => th.zIndex.fab }}>
          <Fab
            variant="extended"
            color="primary"
            onClick={openCreate}
            aria-label={t(
              isCoach ? 'admin.mobile.roster.registerCoach' : 'admin.mobile.roster.registerAthlete',
            )}
          >
            <PersonAddIcon sx={{ mr: 1 }} />
            {t(
              isCoach ? 'admin.mobile.roster.registerCoach' : 'admin.mobile.roster.registerAthlete',
            )}
          </Fab>
        </Box>
      )}

      <AthleteDialog
        open={dialogOpen}
        companyId={companyId}
        athlete={editing}
        isCoach={isCoach}
        onClose={() => setDialogOpen(false)}
        onSaved={onChanged}
      />

      {enrolling && (
        <MobileEnrollDialog
          open={enrolling !== null}
          athlete={enrolling}
          classes={classes}
          enrollments={enrollments}
          onClose={() => setEnrolling(null)}
          onSnapshot={onSnapshot}
        />
      )}
    </Stack>
  );
}
